import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Star, Clock, BookOpen, Trophy, Lock,
  CheckCircle, ChevronRight, Sparkles, Loader2, Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CATEGORY_COLORS = {
  'Emotional Intelligence': { bg: '#FEF3C7', text: '#92400E', accent: '#F59E0B', icon: '🧠' },
  'Strengths & Values': { bg: '#EDE9FE', text: '#5B21B6', accent: '#8B5CF6', icon: '💎' },
  'Financial Literacy': { bg: '#D1FAE5', text: '#065F46', accent: '#10B981', icon: '💰' },
  'Teamwork': { bg: '#DBEAFE', text: '#1E40AF', accent: '#3B82F6', icon: '🤝' },
  'Decision Making': { bg: '#FEE2E2', text: '#991B1B', accent: '#EF4444', icon: '🎯' },
  'Leadership': { bg: '#FCE7F3', text: '#9D174D', accent: '#EC4899', icon: '👑' },
  'Resume & Interview': { bg: '#E0E7FF', text: '#3730A3', accent: '#6366F1', icon: '📝' },
  'College & Career': { bg: '#CFFAFE', text: '#155E75', accent: '#06B6D4', icon: '🚀' },
  'Life Transitions': { bg: '#FFF7ED', text: '#9A3412', accent: '#F97316', icon: '🏠' },
  'Self-Discovery': { bg: '#F3E8FF', text: '#6B21A8', accent: '#A855F7', icon: '🔍' },
  'Career Vision': { bg: '#ECFDF5', text: '#047857', accent: '#34D399', icon: '🌟' },
  'Goal Setting': { bg: '#FEF9C3', text: '#854D0E', accent: '#EAB308', icon: '🔒' },
  'Communication Skills': { bg: '#E0F2FE', text: '#0369A1', accent: '#0EA5E9', icon: '🗣️' },
};

const CoursesListPage = ({ user }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [aiRecs, setAiRecs] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await fetch(`${API}/api/courses/available`, { headers: hdr() });
        if (res.ok) setCourses(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchCourses();
    fetchRecommendations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecommendations = async () => {
    setLoadingRecs(true);
    try {
      const res = await fetch(`${API}/api/ai/course-recommendations`, { headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        setAiRecs(data.recommendations || []);
      }
    } catch (e) { console.error(e); }
    setLoadingRecs(false);
  };

  const filteredCourses = filter === 'all' ? courses
    : filter === 'in_progress' ? courses.filter(c => c.current_module > 0 && !c.completed)
    : filter === 'completed' ? courses.filter(c => c.completed)
    : courses.filter(c => c.current_module === 0);

  const completedCount = courses.filter(c => c.completed).length;
  const inProgressCount = courses.filter(c => c.current_module > 0 && !c.completed).length;
  const totalXP = courses.reduce((s, c) => s + (c.xp_earned || 0), 0);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1a1f3a 100%)' }} className="pb-8 pt-4 px-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15 transition-all" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <div>
              <h1 className="text-xl font-black text-white tracking-tight">Courses</h1>
              <p className="text-xs text-white/50">Level up your skills</p>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <p className="text-2xl font-black text-white">{completedCount}</p>
              <p className="text-[10px] text-white/50 font-medium">Completed</p>
            </div>
            <div className="rounded-2xl p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <p className="text-2xl font-black text-orange-400">{inProgressCount}</p>
              <p className="text-[10px] text-white/50 font-medium">In Progress</p>
            </div>
            <div className="rounded-2xl p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <div className="flex items-center justify-center gap-1">
                <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
                <p className="text-2xl font-black text-amber-400">{totalXP}</p>
              </div>
              <p className="text-[10px] text-white/50 font-medium">XP Earned</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="max-w-lg mx-auto px-4 -mt-4">
        <div className="flex gap-2 p-1.5 rounded-2xl bg-white shadow-sm border border-gray-100">
          {[
            { key: 'all', label: `All (${courses.length})` },
            { key: 'in_progress', label: `Started (${inProgressCount})` },
            { key: 'new', label: 'New' },
            { key: 'completed', label: `Done (${completedCount})` },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${filter === f.key ? 'bg-[#0A0E1A] text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* AI Recommended For You */}
      {aiRecs.length > 0 && filter === 'all' && (
        <div className="max-w-lg mx-auto px-4 mt-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <h2 className="text-sm font-bold text-gray-900">Recommended For You</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
            {aiRecs.map((rec) => {
              const cat = CATEGORY_COLORS[courses.find(c => c.id === rec.course_id)?.category] || { bg: '#F3E8FF', text: '#6B21A8', accent: '#A855F7', icon: '✨' };
              return (
                <div
                  key={rec.course_id}
                  onClick={() => navigate(`/course/${rec.course_id}`)}
                  className="flex-shrink-0 w-56 rounded-2xl border border-purple-100 bg-white p-3 cursor-pointer hover:shadow-md transition-all"
                  data-testid={`ai-rec-${rec.course_id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ backgroundColor: cat.bg }}>
                      {cat.icon}
                    </div>
                    <p className="text-xs font-bold text-gray-900 leading-tight line-clamp-2">{rec.title}</p>
                  </div>
                  <p className="text-[10px] text-gray-500 line-clamp-2">{rec.reason}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loadingRecs && filter === 'all' && (
        <div className="max-w-lg mx-auto px-4 mt-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-purple-500 animate-pulse" />
            <h2 className="text-sm font-bold text-gray-400">Finding courses for you...</h2>
          </div>
        </div>
      )}

      {/* Course Cards */}
      <div className="max-w-lg mx-auto px-4 py-5 space-y-4 pb-24">
        {filteredCourses.map((course, i) => {
          const cat = CATEGORY_COLORS[course.category] || CATEGORY_COLORS['Emotional Intelligence'];
          const progressPct = course.completed ? 100 : course.total_modules > 0 ? Math.round((course.current_module / course.total_modules) * 100) : 0;
          const isStarted = course.current_module > 0;

          return (
            <motion.div key={course.id}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              onClick={() => navigate(`/course/${course.id}`)}
              className="rounded-2xl overflow-hidden cursor-pointer group hover:shadow-lg transition-all border border-gray-100"
              style={{ backgroundColor: 'white' }}
              data-testid={`course-card-${course.id}`}>
              
              {/* Color bar top */}
              <div className="h-1.5" style={{ backgroundColor: cat.accent }} />
              
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Category icon */}
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ backgroundColor: cat.bg }}>
                    {cat.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: cat.accent }}>{course.category}</p>
                      {course.completed && <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />}
                    </div>
                    <h3 className="text-base font-bold text-gray-900 leading-tight">{course.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{course.subtitle}</p>
                  </div>

                  {/* Action indicator */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110 ${course.completed ? 'bg-emerald-50' : isStarted ? 'bg-orange-50' : 'bg-gray-50'}`}>
                    {course.completed ? <Trophy className="w-5 h-5 text-emerald-500" /> : isStarted ? <Play className="w-5 h-5 text-orange-500" fill="currentColor" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                  </div>
                </div>

                {/* Meta row */}
                <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-400 font-medium">
                  <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{course.total_modules} modules</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />~{course.estimated_minutes} min</span>
                  <span className="flex items-center gap-1"><Star className="w-3 h-3" />{course.total_xp} XP</span>
                  {course.badges_earned?.length > 0 && (
                    <span className="flex items-center gap-1 text-amber-500"><Trophy className="w-3 h-3" />{course.badges_earned.length} badges</span>
                  )}
                </div>

                {/* Progress bar (only if started) */}
                {isStarted && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[10px] font-bold" style={{ color: course.completed ? '#10B981' : cat.accent }}>
                        {course.completed ? 'Completed' : `Module ${course.current_module} of ${course.total_modules}`}
                      </p>
                      <p className="text-[10px] font-bold text-gray-400">{progressPct}%</p>
                    </div>
                    <Progress value={progressPct} className="h-1.5" />
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}

        {filteredCourses.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No courses in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesListPage;
